import { requestStatus } from 'src/lib/services/api/request-api';
import { Href } from 'src/components/ui/href';
import { RateRevisionComposedDialog } from 'src/features/revisions/rate-revision-composed-dialog';
import { DialogTrigger } from 'src/components/ui/dialog';
import { If } from 'src/components/If';
import { authQueries } from 'src/api/queries';
import { useDecoratedPostHog, customPosthogEvent } from 'src/providers/posthog';
import { useQueryClient } from '@tanstack/react-query';
import { joinExistingValues } from 'src/helpers/joinExistingValues';
import { useRequestsCounts } from 'src/models';
import { useConfirm } from 'src/components/ConfirmModal';
import { useChatToast } from 'src/components/chat-toast/chat-toast';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useRequestMutations } from 'src/features/requests/use-request-mutations';
import { useToast } from 'src/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/ui/button/button';
import { ButtonLoader } from 'src/components/ui/loader';
import { CheckCircle, Megaphone } from 'lucide-react';
import React from 'react';

const ReEditAndApproveButtons: React.FC<{
  rateRevisionDialogOpen: boolean;
  setRateRevisionDialogOpen: (open: boolean) => void;
}> = ({ rateRevisionDialogOpen, setRateRevisionDialogOpen }) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const request = useDecoratedRequestContext();
  const requestMutations = useRequestMutations();
  const postHog = useDecoratedPostHog();
  const client = useQueryClient();
  const { push } = useChatToast();
  const { update } = useRequestsCounts(
    {},
    {
      enabled: false,
    },
  );
  const { confirm } = useConfirm();

  // latest revision (server data)
  const latestRevision = [...(request?.revisions ?? [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )?.[0];

  const capturePosthogEvent = (rating: number) => {
    const profile = client.getQueryData<any>([authQueries.profile]);
    const company = profile?.company;

    postHog.capture(customPosthogEvent.requestRevisionRated, {
      company: {
        id: company?.id ?? 'unknown',
        name: company?.name ?? 'Unknown',
      },
      request: {
        id: request.id,
        name: request.title ?? 'Unknown',
      },
      editor:
        joinExistingValues([request?.editor?.first_name, request?.editor?.last_name], ' ') ??
        'Unknown',
      requestRevisionRating: rating,
    });
  };

  const updateRequestQueryData = (rating: number) => {
    requestMutations.setQueryData(request.id, (prev) => {
      if (!prev) return prev;

      const next = { ...prev } as typeof prev & {
        rated_revisions_count?: number;
        revisions: Array<{ id: string; rating?: number } & any>;
      };

      next.rated_revisions_count = (next.rated_revisions_count ?? 0) + 1;

      if (next.revisions?.length) {
        next.revisions = next.revisions.map((rev: any) => {
          if (rev.id === latestRevision?.id) {
            return { ...rev, rating };
          }
          return rev;
        });
      }

      return next;
    });
  };

  const handleRevisionRated = (rating: number) => {
    capturePosthogEvent(rating);
    updateRequestQueryData(rating);
  };

  const handleRequestReedit = async () => {
    requestMutations.requestReedit.mutate(request.id, {
      onSuccess: () => {
        push({
          description: "Don't forget to leave a comment for your editor!",
          variant: 'success',
          mode: 'trigger',
        });
        if (request.canRateRevision) setRateRevisionDialogOpen(true);
      },
    });
  };

  return (
    <div className={'tw-flex tw-justify-end tw-gap-2 lg:tw-col-span-12 xl:tw-col-span-12'}>
      {/* Request re-edit / Rate revision flow */}
      <If
        when={request.canRateRevision}
        else={
          <If when={request.canRequestReEdit}>
            <Button
              size={'default'}
              className={'tw-gap-2 tw-whitespace-nowrap'}
              disabled={requestMutations.isAnyMutationLoading}
              onClick={handleRequestReedit}
            >
              <Megaphone className={'tw-size-4'} />
              {t('Re-edit')}
              <ButtonLoader loading={requestMutations.requestReedit.isLoading} />
            </Button>
          </If>
        }
      >
        <RateRevisionComposedDialog
          revision={latestRevision?.id}
          onSuccess={handleRevisionRated}
          open={rateRevisionDialogOpen}
          onOpenChange={setRateRevisionDialogOpen}
        >
          <If when={request.canRequestReEdit}>
            <DialogTrigger asChild>
              <Button
                size={'sm'}
                className={'tw-flex-grow tw-gap-2 tw-whitespace-nowrap'}
                disabled={requestMutations.requestReedit.isLoading}
                onClick={handleRequestReedit}
              >
                <Megaphone className={'tw-size-4'} />
                {t('Re-edit')}
              </Button>
            </DialogTrigger>
          </If>
        </RateRevisionComposedDialog>
      </If>

      {/* Approve */}
      <If when={request.canApprove}>
        <Button
          variant={'success'}
          size={'default'}
          className={'tw-whitespace-nowrap'}
          disabled={requestMutations.isAnyMutationLoading}
          onClick={() => requestMutations.approve.mutate(request.id)}
        >
          {t('Approve')}
          <ButtonLoader loading={requestMutations.approve.isLoading} />
        </Button>
      </If>

      {/* Complete request */}
      <If when={request.canComplete}>
        <Button
          variant={'success'}
          size={'sm'}
          className={'tw-flex-grow tw-gap-2 tw-whitespace-nowrap'}
          disabled={requestMutations.isAnyMutationLoading || !request.canComplete}
          onClick={async () => {
            const confirmed = await confirm('', {
              modal_header: {
                content: t('alerts:confirm.complete_request.title', {
                  defaultValue: 'Approve',
                })!,
              },
              modal_body: {
                content: (
                  <span>
                    {t('alerts:confirm.complete_request_v2', {
                      //this is temporary, fix this directly in the translation service later
                      defaultValue:
                        'Approving will stop all edits and comments. You can still view and download files for 1 month.',
                    })}{' '}
                    <Href
                      href={
                        t('links:other.request_approval_learn_more.url', {
                          defaultValue:
                            'https://help.becreatives.co/what-happens-when-i-complete-my-request',
                        })!
                      }
                      target={'_blank'}
                      variant={'sky'}
                      underline={'hover'}
                    >
                      {t('Read more')}
                    </Href>
                  </span>
                ),
              },
            });

            if (!confirmed) return;

            requestMutations.complete.mutate(request.id, {
              onSuccess: () => {
                update(request.id, requestStatus.complete);
                toast({
                  description: t('alerts:success.request_completed', {
                    defaultValue: 'Request has been completed!',
                  }),
                });
              },
            });
          }}
        >
          <CheckCircle className={'tw-size-4'} />
          {t('alerts:confirm.complete_request.title', { defaultValue: 'Approve' })}
          <ButtonLoader loading={requestMutations.complete.isLoading} />
        </Button>
      </If>
    </div>
  );
};

export default ReEditAndApproveButtons;
